import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Mail, Loader2, Paperclip, Reply, Inbox, Send, AlertCircle, CalendarPlus, Sparkles, ListChecks, CheckSquare, Square, MessageSquareReply, ArrowLeft, X, Zap, Star } from 'lucide-react';
import ContextoComposer from '../ContextoComposer';
import RecipientInput from '../correo/RecipientInput';
import ComposerAiToolbar from '../correo/ComposerAiToolbar';
import AttachmentsInput from '../correo/AttachmentsInput';
import { req } from '../../services/api';


// Saneamiento básico de HTML (mismo patrón que CorreosGmail.jsx).
function sanitizeHtml(html) {
  if (!html) return '';
  let s = String(html);
  s = s.replace(/<script[\s\S]*?<\/script>/gi, '');
  s = s.replace(/<style[\s\S]*?<\/style>/gi, '');
  s = s.replace(/\s+on[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
  s = s.replace(/(href|src)\s*=\s*("|')javascript:[^"']*\2/gi, '$1="#"');
  return s;
}

/**
 * Lista de correos sincronizados filtrados por entidad o por contacto.
 * Props:
 *   - entidadId (opcional): muestra todos los correos vinculados a esa empresa
 *   - contactoEmail (opcional): muestra todos los correos donde aparece ese email
 *   - contextLabel: texto contextual para el header ("empresa Acme" o "Juan Pérez")
 */
export default function CorreosTab({ entidadId, contactoEmail, contextLabel }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cuentas, setCuentas] = useState([]);
  const [composer, setComposer] = useState({ open: false, to: [], cc: [], bcc: [], showCc: false, showBcc: false, subject: '', body: '', cuenta_id: '', adjuntos: [] });
  const [visita, setVisita] = useState({ open: false, msg: null, fecha: '', resumen: '', tipo: 'comercial', loading: false });
  const [tareasIA, setTareasIA] = useState({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false });
  const [sugerencias, setSugerencias] = useState({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null });
  // Iter56 split: mensaje activo en panel derecho + body cargado.
  const [activeMsgId, setActiveMsgId] = useState(null);
  const [activeBody, setActiveBody] = useState({ loading: false, data: null });
  // Detección móvil ligera (sin librería).
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  useEffect(() => {
    const onR = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);

  const cuentasMap = useMemo(() => Object.fromEntries(cuentas.map((c) => [c.id, c])), [cuentas]);
  const activeMsg = useMemo(() => items.find((m) => m.message_id === activeMsgId) || null, [items, activeMsgId]);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entidadId) params.set('entidad_id', entidadId);
      if (contactoEmail) params.set('contacto_email', contactoEmail);
      params.set('limit', '100');
      const r = await req(`/api/correo/sincronizados?${params.toString()}`);
      setItems(r.items || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
    try {
      const c = await req('/api/correo/cuentas');
      setCuentas(Array.isArray(c) ? c : []);
    } catch (e) {
      // No silenciamos el error: si /cuentas falla, el banner "sin cuenta"
      // sería engañoso. Mostramos toast y dejamos el array vacío explícito.
      console.error('Error cargando cuentas:', e);
      toast.error(`No se pudieron cargar tus cuentas de correo: ${e.message}`);
      setCuentas([]);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [entidadId, contactoEmail]);

  // Cuando cambian los items, mantener mensaje activo válido.
  useEffect(() => {
    if (activeMsgId && !items.find((m) => m.message_id === activeMsgId)) {
      setActiveMsgId(null);
      setActiveBody({ loading: false, data: null });
    }
  }, [items, activeMsgId]);

  const seleccionarMensaje = useCallback(async (m) => {
    setActiveMsgId(m.message_id);
    setActiveBody({ loading: true, data: null });
    try {
      const cuentaId = m.cuenta_id || cuentas[0]?.id;
      if (!cuentaId) {
        setActiveBody({ loading: false, data: { body_text: m.preview || '' } });
        return;
      }
      const d = await req(`/api/correo/cuentas/${cuentaId}/mensajes/${encodeURIComponent(m.message_id)}/body`);
      setActiveBody({ loading: false, data: d });
    } catch (e) {
      console.error('body', e);
      setActiveBody({ loading: false, data: { body_text: m.preview || '' } });
    }
    // Marcar leído si no lo estaba (silencioso).
    if (m.is_read === false) {
      try {
        await req(`/api/correo/mensajes/${encodeURIComponent(m.message_id)}/leido?leido=true`, { method: 'PATCH' });
        setItems((prev) => prev.map((x) => x.message_id === m.message_id ? { ...x, is_read: true } : x));
      } catch (_) { /* silent */ }
    }
  }, [cuentas]);

  const cerrarLectura = () => {
    setActiveMsgId(null);
    setActiveBody({ loading: false, data: null });
  };

  const responder = (m) => {
    const to = m.from_address || (m.to_addresses || [])[0] || '';
    setComposer({
      open: true,
      to: to ? [to] : [],
      cc: [], bcc: [], showCc: false, showBcc: false,
      subject: m.subject?.startsWith('Re:') ? m.subject : `Re: ${m.subject || ''}`,
      body: `\n\n--- Mensaje original ---\n${m.preview || ''}`,
      cuenta_id: cuentas.length > 0 ? cuentas[0].id : '',
      adjuntos: [],
    });
  };

  // Sugerencias simples sin LLM: detecta menciones "fecha + hora", "reunión", "visita", "el día"
  // Devuelve datetime-local string ('YYYY-MM-DDTHH:mm') o vacío.
  const inferirFechaDesdeMensaje = (texto) => {
    if (!texto) return '';
    const m = texto.match(/(\d{1,2})[\/-](\d{1,2})(?:[\/-](\d{2,4}))?(?:\s+(?:a\s+las\s+|alas\s+|@\s*)?(\d{1,2})(?::(\d{2}))?)?/i);
    if (!m) return '';
    const day = m[1].padStart(2, '0');
    const mon = m[2].padStart(2, '0');
    let yr = m[3] ? (m[3].length === 2 ? `20${m[3]}` : m[3]) : new Date().getFullYear().toString();
    const hh = (m[4] || '10').padStart(2, '0');
    const mm = (m[5] || '00').padStart(2, '0');
    return `${yr}-${mon}-${day}T${hh}:${mm}`;
  };

  const abrirCrearVisita = (m) => {
    const fechaSug = inferirFechaDesdeMensaje((m.subject || '') + ' ' + (m.preview || ''));
    setVisita({
      open: true, msg: m,
      fecha: fechaSug || new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 16),
      resumen: `Visita propuesta a partir del correo de ${m.from_name || m.from_address}.\n\nAsunto: ${m.subject}\n\nFragmento:\n${m.preview || ''}`,
      tipo: 'comercial',
      loading: false,
    });
  };

  const abrirExtraerTareas = async (m) => {
    setTareasIA({ open: true, loading: true, msg: m, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false });
    try {
      const r = await req('/api/correo/ai/extraer-tareas', {
        method: 'POST', body: JSON.stringify({ message_id: m.message_id }),
      });
      const propuestas = r.tareas_propuestas || [];
      // Por defecto seleccionamos todas las propuestas
      const sel = new Set(propuestas.map((_, i) => i));
      setTareasIA({ open: true, loading: false, msg: m, propuestas, seleccionadas: sel, resumen: r.resumen_correo || '', creando: false });
    } catch (e) {
      toast.error(`Gemini error: ${e.message}`);
      setTareasIA({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false });
    }
  };

  const abrirSugerencias = async (m) => {
    setSugerencias({ open: true, loading: true, msg: m, borradores: [], perfilAplicado: false, contexto: null });
    try {
      const r = await req('/api/correo/ai/sugerir-respuestas', {
        method: 'POST', body: JSON.stringify({ message_id: m.message_id }),
      });
      setSugerencias({
        open: true, loading: false, msg: m,
        borradores: r.borradores || [],
        perfilAplicado: !!r.perfil_aplicado,
        contexto: r.contexto || null,
      });
    } catch (e) {
      toast.error(`Gemini: ${e.message}`);
      setSugerencias({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null });
    }
  };

  const usarBorrador = (b) => {
    const m = sugerencias.msg;
    setComposer({
      open: true,
      to: m?.from_address ? [m.from_address] : [],
      cc: [], bcc: [], showCc: false, showBcc: false,
      subject: b.asunto || (m?.subject?.startsWith('Re:') ? m.subject : `Re: ${m?.subject || ''}`),
      body: b.cuerpo || '',
      cuenta_id: cuentas.length > 0 ? cuentas[0].id : '',
      adjuntos: [],
    });
    setSugerencias({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null });
  };

  // aiAsistir y aiTool movidos a ComposerAiToolbar (componente compartido).

  const toggleTareaSel = (i) => {
    setTareasIA((t) => {
      const sel = new Set(t.seleccionadas);
      if (sel.has(i)) sel.delete(i); else sel.add(i);
      return { ...t, seleccionadas: sel };
    });
  };

  const crearTareasSeleccionadas = async () => {
    const m = tareasIA.msg;
    const eid = entidadId || m?.vinculado_entidad_id;
    if (!eid) {
      toast.error('No hay empresa asociada al correo. Abre el tab Correos desde la ficha de la empresa.');
      return;
    }
    setTareasIA((t) => ({ ...t, creando: true }));
    let creadas = 0; let fallos = 0;
    for (let i = 0; i < tareasIA.propuestas.length; i++) {
      if (!tareasIA.seleccionadas.has(i)) continue;
      const t = tareasIA.propuestas[i];
      try {
        await req('/api/tareas', {
          method: 'POST',
          body: JSON.stringify({
            titulo: t.titulo,
            descripcion: t.descripcion + (m?.subject ? `\n\n📧 Origen: correo "${m.subject}"` : ''),
            entidad_id: eid,
            contacto_id: m?.vinculado_contacto_id || null,
            vencimiento: t.fecha_propuesta || null,
            prioridad: t.prioridad,
            urgente: t.urgente ?? false,
            importante: t.importante ?? false,
          }),
        });
        creadas++;
      } catch (e) { fallos++; console.error('crear tarea fallo', e); }
    }
    if (creadas > 0) toast.success(`${creadas} tarea(s) creada(s)${fallos ? ` · ${fallos} fallaron` : ''}`);
    if (creadas === 0 && fallos > 0) toast.error(`No se pudo crear ninguna tarea (${fallos} fallos)`);
    setTareasIA({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false });
  };

  const guardarVisita = async () => {
    if (!entidadId) {
      // Si estamos en un contacto sin entidad explícita, derivamos la entidad
      // desde la vinculación del propio mensaje
      const linked = visita.msg?.vinculado_entidad_id;
      if (!linked) {
        toast.error('No hay empresa asociada a este correo. Crea la visita desde la ficha de la empresa.');
        return;
      }
    }
    const eid = entidadId || visita.msg?.vinculado_entidad_id;
    setVisita((v) => ({ ...v, loading: true }));
    try {
      // Solo fecha (la API espera date YYYY-MM-DD)
      const fechaSolo = visita.fecha.slice(0, 10);
      const contactoId = visita.msg?.vinculado_contacto_id;
      await req(`/api/visitas/entidad/${eid}`, {
        method: 'POST',
        body: JSON.stringify({
          fecha: fechaSolo,
          tipo: visita.tipo,
          contactos_ids: contactoId ? [contactoId] : [],
          resumen: visita.resumen,
        }),
      });
      toast.success('Visita creada y evento añadido a tu calendario');
      setVisita({ open: false, msg: null, fecha: '', resumen: '', tipo: 'comercial', loading: false });
    } catch (e) {
      toast.error(`Error: ${e.message}`);
      setVisita((v) => ({ ...v, loading: false }));
    }
  };

  const enviar = async () => {
    if (!composer.cuenta_id) { toast.error('Selecciona una cuenta de envío'); return; }
    if (composer.to.length === 0) { toast.error('Añade al menos un destinatario'); return; }
    try {
      const fd = new FormData();
      fd.append('to', JSON.stringify(composer.to));
      fd.append('subject', composer.subject);
      fd.append('body', composer.body);
      if (composer.cc.length > 0) fd.append('cc', JSON.stringify(composer.cc));
      if (composer.bcc.length > 0) fd.append('bcc', JSON.stringify(composer.bcc));
      for (const f of (composer.adjuntos || [])) fd.append('attachments', f, f.name);
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/correo/cuentas/${composer.cuenta_id}/enviar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      const ad = composer.adjuntos?.length || 0;
      toast.success(`Correo enviado${ad ? ` con ${ad} adjunto(s)` : ''}`);
      setComposer({ open: false, to: [], cc: [], bcc: [], showCc: false, showBcc: false, subject: '', body: '', cuenta_id: '', adjuntos: [] });
      setTimeout(cargar, 800);
    } catch (e) { toast.error(`Envío falló: ${e.message}`); }
  };

  if (cuentas.length === 0 && !loading) {
    return (
      <Card className="p-6 border-dashed border-amber-300 bg-amber-50/40" data-testid="correos-tab-sin-cuenta">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-ink-900 mb-1">Sin cuenta de correo conectada</h3>
            <p className="text-sm text-ink-600">
              Conecta tu correo en <a href="/perfil" className="text-brand-600 underline">Mi perfil</a> para ver
              aquí los correos intercambiados con {contextLabel || 'esta entidad'}.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="correos-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-ink-600">
          <Inbox className="w-4 h-4" />
          {loading ? 'Cargando…' : `${items.length} correo(s) sincronizado(s)`}
        </div>
        <Button size="sm" variant="outline" onClick={() => setComposer({ open: true, to: contactoEmail ? [contactoEmail] : [], cc: [], bcc: [], showCc: false, showBcc: false, subject: '', body: '', cuenta_id: cuentas[0]?.id, adjuntos: [] })} data-testid="correos-tab-nuevo">
          <Send className="w-4 h-4 mr-1.5" /> Nuevo correo
        </Button>
      </div>

      {loading && <div className="text-sm text-ink-500 py-8 text-center"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Cargando correos…</div>}

      {!loading && items.length === 0 && (
        <Card className="p-6 text-center text-sm text-ink-500 border-dashed" data-testid="correos-tab-vacio">
          No hay correos sincronizados con {contextLabel || 'esta entidad'} todavía.
          <br />
          <span className="text-xs">Los correos se sincronizan al abrir la bandeja en <a href="/perfil" className="text-brand-600 underline">Mi perfil</a>.</span>
        </Card>
      )}

      {!loading && items.length > 0 && (
        <div
          className="flex flex-col md:flex-row gap-3 border border-ink-200 rounded-lg bg-white overflow-hidden"
          style={{ height: 'min(70vh, 640px)' }}
          data-testid="correos-tab-split"
        >
          {/* Lista correos */}
          <div className={`md:w-[38%] md:min-w-[280px] md:max-w-[420px] md:border-r border-ink-200 overflow-y-auto ${isMobile && activeMsg ? 'hidden' : 'block'}`} data-testid="correos-tab-lista">
            <ul className="divide-y divide-ink-100">
              {items.map((m) => {
                const isActive = m.message_id === activeMsgId;
                return (
                  <li
                    key={m.message_id}
                    onClick={() => seleccionarMensaje(m)}
                    className={`px-3 py-2 cursor-pointer transition flex flex-col gap-0.5 ${isActive ? 'bg-brand-50 border-l-2 border-l-brand-500' : m.is_read === false ? 'bg-white hover:bg-ink-50 font-medium' : 'bg-ink-50/40 hover:bg-ink-50'}`}
                    data-testid={`correo-${m.message_id}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`text-xs truncate flex-1 ${m.is_read === false ? 'text-ink-900 font-semibold' : 'text-ink-700'}`}>
                        {m.from_name || m.from_address || '(sin remitente)'}
                      </div>
                      <div className="text-[10px] text-ink-400 shrink-0">
                        {m.received_at ? new Date(m.received_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs truncate flex-1 ${m.is_read === false ? 'text-ink-900' : 'text-ink-600'}`}>
                        {m.subject || '(sin asunto)'}
                      </span>
                      {m.has_attachments && <Paperclip className="w-3 h-3 text-ink-400 shrink-0" />}
                      {m.es_oportunidad && <span className="text-[9px]" title="Oportunidad detectada">🎯</span>}
                    </div>
                    {m.preview && <div className="text-[11px] text-ink-500 truncate">{m.preview}</div>}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Panel lectura */}
          <div className={`flex-1 flex flex-col min-h-0 bg-ink-50/30 ${isMobile && !activeMsg ? 'hidden' : 'flex'}`} data-testid="correos-tab-lectura">
            {!activeMsg ? (
              <div className="flex-1 flex items-center justify-center text-ink-400">
                <div className="text-center px-4">
                  <Mail className="w-10 h-10 mx-auto mb-2 text-ink-300" />
                  <p className="text-xs">Selecciona un correo</p>
                </div>
              </div>
            ) : (
              <>
                {/* Header del correo activo */}
                <div className="px-4 py-3 border-b border-ink-200 bg-white flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {isMobile && (
                        <Button size="sm" variant="ghost" onClick={cerrarLectura} className="h-6 -ml-1 px-1" data-testid="correos-tab-volver">
                          <ArrowLeft className="w-4 h-4" />
                        </Button>
                      )}
                      <h2 className="text-sm font-semibold text-ink-900 truncate flex-1">{activeMsg.subject || '(sin asunto)'}</h2>
                      {activeMsg.es_oportunidad && <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] border">🎯</Badge>}
                    </div>
                    <div className="text-xs text-ink-600 mt-0.5">
                      <span className="font-medium">{activeMsg.from_name || activeMsg.from_address}</span>
                      {activeMsg.from_name && <span className="text-ink-400"> &lt;{activeMsg.from_address}&gt;</span>}
                      <span className="mx-1.5 text-ink-300">·</span>
                      <span className="text-ink-500">{activeMsg.received_at ? new Date(activeMsg.received_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : ''}</span>
                    </div>
                  </div>
                  {!isMobile && (
                    <Button size="sm" variant="ghost" onClick={cerrarLectura} className="h-7" title="Cerrar" data-testid="correos-tab-cerrar">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Toolbar acciones */}
                <div className="px-4 py-1.5 border-b border-ink-100 bg-white flex items-center gap-1 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => responder(activeMsg)} className="h-7 text-xs" data-testid={`responder-${activeMsg.message_id}`}>
                    <Reply className="w-3.5 h-3.5 mr-1" /> Responder
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => abrirSugerencias(activeMsg)} className="h-7 text-xs text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-50" data-testid={`sugerir-respuestas-${activeMsg.message_id}`}>
                    <MessageSquareReply className="w-3.5 h-3.5 mr-1" /> Sugerir
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => abrirExtraerTareas(activeMsg)} className="h-7 text-xs text-violet-700 border-violet-200 hover:bg-violet-50" data-testid={`extraer-tareas-${activeMsg.message_id}`}>
                    <Sparkles className="w-3.5 h-3.5 mr-1" /> Extraer tareas
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => abrirCrearVisita(activeMsg)} className="h-7 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`crear-visita-${activeMsg.message_id}`}>
                    <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Visita
                  </Button>
                </div>

                {/* Cuerpo */}
                <div className="flex-1 overflow-y-auto px-4 py-3 bg-white" data-testid="correos-tab-cuerpo">
                  {activeBody.loading ? (
                    <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-brand-600 mx-auto" /></div>
                  ) : activeBody.data?.body_html ? (
                    <div
                      className="prose prose-sm max-w-none text-ink-800"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(activeBody.data.body_html) }}
                      data-testid="correos-tab-body-html"
                    />
                  ) : (
                    <pre className="whitespace-pre-wrap font-sans text-sm text-ink-800" data-testid="correos-tab-body-text">
                      {activeBody.data?.body_text || activeMsg.preview || '(sin contenido)'}
                    </pre>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {sugerencias.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSugerencias({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null })}>
          <Card className="max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="dialog-sugerencias">
            <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <MessageSquareReply className="w-5 h-5 text-fuchsia-600" /> Borradores de respuesta
              {sugerencias.perfilAplicado && (
                <Badge className="bg-violet-50 text-violet-700 border-violet-200 text-[9px] border ml-1">Tu estilo</Badge>
              )}
              {sugerencias.contexto && sugerencias.contexto.vinculado && (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px] border ml-1">+ Contexto CRM</Badge>
              )}
            </h3>
            <p className="text-xs text-ink-500 mb-3">Elige el que más se ajuste y se cargará en el composer para que termines de afinarlo antes de enviar.</p>

            {sugerencias.msg && (
              <div className="bg-ink-50 border border-ink-200 rounded p-2 mb-3 text-xs">
                <div className="font-semibold text-ink-700 truncate">📧 {sugerencias.msg.subject || '(sin asunto)'}</div>
                <div className="text-ink-500 mt-0.5">De: {sugerencias.msg.from_name || sugerencias.msg.from_address}</div>
              </div>
            )}

            {sugerencias.contexto && sugerencias.contexto.vinculado && (
              <div className="mb-3" data-testid="sugerencias-contexto">
                <ContextoComposer email={sugerencias.msg?.from_address} prefilled={sugerencias.contexto} />
              </div>
            )}

            {sugerencias.loading && (
              <div className="py-12 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-fuchsia-600 mx-auto mb-2" />
                <p className="text-sm text-ink-500">Gemini está generando borradores con tu estilo…</p>
              </div>
            )}

            {!sugerencias.loading && sugerencias.borradores.length === 0 && (
              <div className="py-8 text-center text-ink-500 text-sm" data-testid="ia-sin-borradores">
                Gemini no devolvió borradores. Inténtalo de nuevo.
              </div>
            )}

            {!sugerencias.loading && sugerencias.borradores.length > 0 && (
              <div className="space-y-2" data-testid="ia-borradores-list">
                {sugerencias.borradores.map((b, i) => (
                  <div
                    key={i}
                    className="p-3 border border-ink-200 rounded hover:border-fuchsia-300 transition cursor-pointer bg-white"
                    onClick={() => usarBorrador(b)}
                    data-testid={`ia-borrador-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 text-[10px] border">Opción {i + 1}</Badge>
                      <span className="font-medium text-ink-900 text-sm">{b.titulo}</span>
                    </div>
                    <div className="text-xs text-ink-600 mb-1"><span className="font-semibold">Asunto:</span> {b.asunto}</div>
                    <pre className="text-xs text-ink-700 whitespace-pre-wrap font-sans line-clamp-6 bg-ink-50/50 rounded p-2 border border-ink-100">{b.cuerpo}</pre>
                    <div className="mt-2 text-right">
                      <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid={`usar-borrador-${i}`}>
                        Usar este borrador →
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => setSugerencias({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null })}>Cerrar</Button>
            </div>
          </Card>
        </div>
      )}

      {tareasIA.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setTareasIA({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false })}>
          <Card className="max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="dialog-extraer-tareas">
            <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" /> Tareas detectadas por Gemini
            </h3>
            <p className="text-xs text-ink-500 mb-3">Revisa, marca/desmarca y crea las que tengan sentido. Las tareas creadas se asocian a la empresa y al contacto vinculado del correo.</p>

            {tareasIA.msg && (
              <div className="bg-ink-50 border border-ink-200 rounded p-2 mb-3 text-xs">
                <div className="font-semibold text-ink-700 truncate">📧 {tareasIA.msg.subject || '(sin asunto)'}</div>
                <div className="text-ink-500 mt-0.5">De: {tareasIA.msg.from_name || tareasIA.msg.from_address}</div>
              </div>
            )}

            {tareasIA.loading && (
              <div className="py-12 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-violet-600 mx-auto mb-2" />
                <p className="text-sm text-ink-500">Gemini está leyendo el correo y extrayendo tareas…</p>
              </div>
            )}

            {!tareasIA.loading && tareasIA.resumen && (
              <div className="mb-3 p-3 bg-violet-50 border border-violet-200 rounded text-xs text-violet-900">
                <div className="font-semibold mb-1 uppercase tracking-wider text-[10px]">Resumen IA</div>
                {tareasIA.resumen}
              </div>
            )}

            {!tareasIA.loading && tareasIA.propuestas.length === 0 && (
              <div className="py-8 text-center text-ink-500 text-sm" data-testid="ia-sin-tareas">
                Gemini no ha detectado tareas concretas en este correo.
              </div>
            )}

            {!tareasIA.loading && tareasIA.propuestas.length > 0 && (
              <div className="space-y-2" data-testid="ia-tareas-list">
                {tareasIA.propuestas.map((t, i) => {
                  const sel = tareasIA.seleccionadas.has(i);
                  return (
                    <div
                      key={i}
                      onClick={() => toggleTareaSel(i)}
                      className={`p-3 border rounded cursor-pointer transition ${sel ? 'border-violet-400 bg-violet-50/50' : 'border-ink-200 bg-white hover:border-ink-300'}`}
                      data-testid={`ia-tarea-${i}`}
                    >
                      <div className="flex items-start gap-2">
                        {sel ? <CheckSquare className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" /> : <Square className="w-4 h-4 text-ink-300 mt-0.5 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-ink-900">{t.titulo}</span>
                            <Badge className={`text-[9px] border ${
                              t.prioridad === 'alta' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                              t.prioridad === 'baja' ? 'bg-ink-50 text-ink-600 border-ink-200' :
                              'bg-amber-50 text-amber-700 border-amber-200'
                            }`}>{t.prioridad}</Badge>
                            <Badge className="text-[9px] bg-ink-50 text-ink-600 border-ink-200 border">{t.categoria}</Badge>
                            {t.fecha_propuesta && <Badge className="text-[9px] bg-blue-50 text-blue-700 border-blue-200 border">📅 {t.fecha_propuesta}</Badge>}
                            {t.urgente && <Badge className="text-[9px] bg-red-50 text-red-700 border-red-200 border flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" />Urgente</Badge>}
                            {t.importante && <Badge className="text-[9px] bg-violet-50 text-violet-700 border-violet-200 border flex items-center gap-0.5"><Star className="w-2.5 h-2.5" />Importante</Badge>}
                          </div>
                          <p className="text-xs text-ink-600 mt-1">{t.descripcion}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!tareasIA.loading && tareasIA.propuestas.length > 0 && (
              <div className="flex justify-between items-center mt-4 gap-2">
                <span className="text-xs text-ink-500">{tareasIA.seleccionadas.size} de {tareasIA.propuestas.length} seleccionadas</span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setTareasIA({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false })}>Cancelar</Button>
                  <Button
                    onClick={crearTareasSeleccionadas}
                    disabled={tareasIA.seleccionadas.size === 0 || tareasIA.creando}
                    className="bg-violet-600 hover:bg-violet-700"
                    data-testid="ia-crear-tareas"
                  >
                    {tareasIA.creando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando…</> : <><ListChecks className="w-4 h-4 mr-1.5" /> Crear {tareasIA.seleccionadas.size} tarea(s)</>}
                  </Button>
                </div>
              </div>
            )}

            {!tareasIA.loading && tareasIA.propuestas.length === 0 && (
              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={() => setTareasIA({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false })}>Cerrar</Button>
              </div>
            )}
          </Card>
        </div>
      )}

      {visita.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setVisita({ open: false, msg: null, fecha: '', resumen: '', tipo: 'comercial', loading: false })}>
          <Card className="max-w-xl w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="dialog-crear-visita">
            <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-emerald-600" /> Crear visita desde correo
            </h3>
            <p className="text-xs text-ink-500 mb-3">
              Genera una visita prellenada con los datos del correo. Si tienes calendario conectado,
              se creará el evento automáticamente y se invitará al contacto vinculado.
            </p>

            <div className="bg-ink-50 border border-ink-200 rounded p-2 mb-3 text-xs">
              <div className="font-semibold text-ink-700 truncate">📧 {visita.msg?.subject || '(sin asunto)'}</div>
              <div className="text-ink-500 mt-0.5">De: {visita.msg?.from_name || visita.msg?.from_address}</div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-ink-500 uppercase tracking-wider">Fecha y hora propuestas</label>
                <input
                  type="datetime-local"
                  value={visita.fecha}
                  onChange={(e) => setVisita({ ...visita, fecha: e.target.value })}
                  className="w-full mt-1 border border-ink-200 rounded px-3 py-2 text-sm"
                  data-testid="visita-fecha"
                />
                <p className="text-[11px] text-ink-400 mt-1">
                  💡 Si el correo menciona una fecha (ej. "el 15/05 a las 10h"), la rellenamos automáticamente.
                </p>
              </div>
              <div>
                <label className="text-xs text-ink-500 uppercase tracking-wider">Tipo</label>
                <select
                  value={visita.tipo}
                  onChange={(e) => setVisita({ ...visita, tipo: e.target.value })}
                  className="w-full mt-1 border border-ink-200 rounded px-3 py-2 text-sm bg-white"
                  data-testid="visita-tipo"
                >
                  <option value="comercial">Comercial</option>
                  <option value="tecnica">Técnica</option>
                  <option value="postventa">Post-venta</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-ink-500 uppercase tracking-wider">Resumen / Notas</label>
                <textarea
                  rows={5}
                  value={visita.resumen}
                  onChange={(e) => setVisita({ ...visita, resumen: e.target.value })}
                  className="w-full mt-1 border border-ink-200 rounded px-3 py-2 text-sm"
                  data-testid="visita-resumen"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setVisita({ open: false, msg: null, fecha: '', resumen: '', tipo: 'comercial', loading: false })}>Cancelar</Button>
              <Button
                onClick={guardarVisita}
                disabled={!visita.fecha || !visita.resumen || visita.loading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="visita-guardar"
              >
                {visita.loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando…</> : <><CalendarPlus className="w-4 h-4 mr-1.5" /> Crear visita</>}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {composer.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setComposer({ open: false, to: [], cc: [], bcc: [], showCc: false, showBcc: false, subject: '', body: '', cuenta_id: '', adjuntos: [] }); }}>
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5" onMouseDown={(e) => e.stopPropagation()} data-testid="composer-correo">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2"><Send className="w-5 h-5" /> Nuevo correo</h3>
            {composer.to.length > 0 && composer.to[0].includes('@') && (
              <div className="mb-3" data-testid="contexto-composer-wrap">
                <ContextoComposer email={composer.to[0]} compact />
              </div>
            )}
            <div className="space-y-3">
              {cuentas.length > 1 && (
                <div>
                  <label className="text-xs text-ink-500">Desde</label>
                  <select className="w-full mt-1 border border-ink-200 rounded px-3 py-2 text-sm bg-white" value={composer.cuenta_id} onChange={(e) => setComposer({ ...composer, cuenta_id: e.target.value })}>
                    {cuentas.map((c) => <option key={c.id} value={c.id}>{c.email} ({c.tipo})</option>)}
                  </select>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-ink-500">Para</label>
                  <div className="flex items-center gap-1">
                    {!composer.showCc && (
                      <button type="button" onClick={() => setComposer({ ...composer, showCc: true })} className="text-[11px] text-brand-600 hover:underline" data-testid="composer-add-cc">+ CC</button>
                    )}
                    {!composer.showBcc && (
                      <button type="button" onClick={() => setComposer({ ...composer, showBcc: true })} className="text-[11px] text-brand-600 hover:underline" data-testid="composer-add-bcc">+ CCO</button>
                    )}
                  </div>
                </div>
                <RecipientInput
                  value={composer.to}
                  onChange={(v) => setComposer({ ...composer, to: v })}
                  placeholder="nombre o email…"
                  testid="composer-to"
                />
              </div>
              {composer.showCc && (
                <div>
                  <label className="text-xs text-ink-500">CC</label>
                  <RecipientInput
                    value={composer.cc}
                    onChange={(v) => setComposer({ ...composer, cc: v })}
                    placeholder="copia (CC)…"
                    testid="composer-cc"
                  />
                </div>
              )}
              {composer.showBcc && (
                <div>
                  <label className="text-xs text-ink-500">CCO (oculta)</label>
                  <RecipientInput
                    value={composer.bcc}
                    onChange={(v) => setComposer({ ...composer, bcc: v })}
                    placeholder="copia oculta (CCO)…"
                    testid="composer-bcc"
                  />
                </div>
              )}
              <div>
                <label className="text-xs text-ink-500">Asunto</label>
                <input className="w-full mt-1 border border-ink-200 rounded px-3 py-2 text-sm" value={composer.subject} onChange={(e) => setComposer({ ...composer, subject: e.target.value })} data-testid="composer-subject" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-ink-500">Mensaje</label>
                  <ComposerAiToolbar body={composer.body} onChange={(nb) => setComposer((c) => ({ ...c, body: nb }))} />
                </div>
                <textarea rows={8} className="w-full mt-1 border border-ink-200 rounded px-3 py-2 text-sm" value={composer.body} onChange={(e) => setComposer({ ...composer, body: e.target.value })} data-testid="composer-body" />
                <p className="text-[10px] text-ink-400 mt-1">La IA usa tu estilo aprendido (si está disponible) y nunca añade firmas que no sean tuyas.</p>
              </div>
              <div>
                <label className="text-xs text-ink-500 block mb-1">Adjuntos</label>
                <AttachmentsInput
                  value={composer.adjuntos || []}
                  onChange={(v) => setComposer((c) => ({ ...c, adjuntos: v }))}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setComposer({ open: false, to: [], cc: [], bcc: [], showCc: false, showBcc: false, subject: '', body: '', cuenta_id: '', adjuntos: [] })}>Cancelar</Button>
              <Button onClick={enviar} disabled={composer.to.length === 0 || !composer.subject} data-testid="composer-enviar">
                <Send className="w-4 h-4 mr-1.5" /> Enviar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
